/*
 * Copyright Mission 42 Ventures, Inc. All rights reserved.
 * Unauthorized copying, distribution, or use is prohibited.
 */

// Consumption arcs for the Global point cloud (PLAT-1646): who calls whom,
// from the catalog's declared API relations — the one source that actually
// records cross-repo consumption today (symbol-level cross-repo edges wait
// on upstream namespace extraction, PLAT-1650). Provenance is part of the
// contract: these arcs are ALWAYS labeled as catalog-declared, never implied
// to come from the symbol graphs.

export type CatalogEntityLike = {
  kind: string;
  metadata: {
    name: string;
    namespace?: string;
    annotations?: Record<string, string>;
  };
  relations?: { type: string; targetRef: string }[];
};

export type ConsumptionArc = {
  from: string; // consumer repo slug (org/name)
  to: string; // provider repo slug
  apis: string[];
  crossOrg: boolean;
};

const PROJECT_SLUG = 'github.com/project-slug';

const refOf = (e: CatalogEntityLike): string =>
  `${e.kind}:${e.metadata.namespace ?? 'default'}/${e.metadata.name}`.toLowerCase();

// consumer --consumesApi--> API --providesApi(inverse)--> provider, each
// component mapped to its repo by the project-slug annotation. Self-loops
// (a repo consuming its own API) are dropped: the cloud shows flow BETWEEN
// repos. Pairs aggregate; the API names ride along for the hover chip.
export const buildConsumptionArcs = (
  entities: CatalogEntityLike[],
  repoSlugs: Set<string>,
): { arcs: ConsumptionArc[]; unmapped: number } => {
  const slugOf = new Map<string, string>();
  const providerOfApi = new Map<string, string>();
  for (const e of entities) {
    if (e.kind.toLowerCase() !== 'component') continue;
    const slug = e.metadata.annotations?.[PROJECT_SLUG]?.toLowerCase();
    const ref = refOf(e);
    if (slug) slugOf.set(ref, slug);
    for (const rel of e.relations ?? []) {
      if (rel.type === 'providesApi') {
        providerOfApi.set(rel.targetRef.toLowerCase(), ref);
      }
    }
  }
  const byPair = new Map<string, ConsumptionArc>();
  let unmapped = 0;
  for (const e of entities) {
    if (e.kind.toLowerCase() !== 'component') continue;
    const from = slugOf.get(refOf(e));
    for (const rel of e.relations ?? []) {
      if (rel.type !== 'consumesApi') continue;
      const apiRef = rel.targetRef.toLowerCase();
      const provider = providerOfApi.get(apiRef);
      const to = provider ? slugOf.get(provider) : undefined;
      if (!from || !to || !repoSlugs.has(from) || !repoSlugs.has(to)) {
        unmapped++;
        continue;
      }
      if (from === to) continue;
      const k = `${from}\u0000${to}`;
      const arc = byPair.get(k) ?? {
        from,
        to,
        apis: [],
        crossOrg: from.split('/')[0] !== to.split('/')[0],
      };
      const apiName = apiRef.split('/').pop() ?? apiRef;
      if (!arc.apis.includes(apiName)) arc.apis.push(apiName);
      byPair.set(k, arc);
    }
  }
  return { arcs: [...byPair.values()], unmapped };
};
